/**
 * Domain Compiler (`domain-def@4`) — the ONE generic compiler.
 *
 * Compiles a `DomainDefinitionJSON` into the superset `DomainRuntime`. This is
 * the single compiler the whole platform adopts: it grafts the career/dna
 * lineage's generic `scope.infer` type layer onto the framework lineage's rich
 * sub-compilers (authz, router, UMA, OpenAPI, client).
 *
 * Generics:
 * - `D` — the domain definition, captured `const` so a caller's
 *   `as const satisfies DomainDefinitionJSON` literal survives (this is what
 *   preserves `D["types"]` instead of widening to `Record<string, unknown>`).
 * - `P` — the app's injected primitive set (career passes `careerPrimitives`,
 *   dna `uneqPrimitives`; default `{}`). Merged into the ArkType scope after
 *   `coreDomain` and before the domain's own types.
 *
 * The MANDATORY inference lever (see `docs/type-safety-investigation.md`): the
 * `types` field is explicitly typed `Module<scope.infer<MergedScope<D, P>>>` via
 * the `DomainRuntime<D, P>` return annotation. TS cannot infer that through the
 * scope-input spread on its own.
 *
 * The generic `P` forces ONE concession the career (concrete-primitives)
 * compiler didn't need: ArkType's `scope.validate` rejects a *bare generic*
 * index signature (`P extends Record<string, unknown>` resolves its value to
 * `unknown`, which is not a valid def), while it accepts the deferred
 * indexed-access `D["types"]` and the spread-inferred core+types shape. So the
 * runtime object (which DOES carry the injected primitives) is fed to `scope()`
 * typed as that accepted core+types shape, and the exported module is then
 * ASSERTED to the full `Module<scope.infer<MergedScope<D, P>>>`. That assertion
 * PRESERVES inference (it names the module's true, full type) — it is NOT one of
 * the forbidden inference-killing casts (`scope(… as never)` or
 * `as unknown as Record<string, Type>` on the module). The only other cast is a
 * runtime-only `typesRecord` view INSIDE the body, for the string-keyed
 * `resolveType` lookups.
 *
 * @see DomainDefinitionJSON for the input schema.
 * @see DomainRuntime for the output interface.
 */

import { scope, type, type Type, type Module } from "arktype";
import type { DomainDefinitionJSON } from "../schema/domain-definition";
import {
  coreDomain,
  type DomainRuntime,
  type MergedScope,
  type ResourceAccessor,
  type ValidationResult,
  type RouteContext,
  type AuthzResult,
  type UrnParams,
} from "../runtime/domain-runtime";
import { compileAuthzRules, checkAuthz as checkAuthzRule } from "./compile-authz";
import { compileRoutes } from "./compile-router";
import { compileUmaResources } from "./compile-uma";
import { compileOpenAPI } from "./compile-openapi";
import { createClient } from "./generate-client";

/**
 * Parse URN parameters based on a pattern.
 *
 * @example
 * parseUrn("urn:bible:org-123:summary", "urn:bible:{organizationId}:summary")
 * // => { organizationId: "org-123" }
 */
export function parseUrn(urn: string, pattern: string): UrnParams {
  const regexPattern = pattern.replace(
    /\{(\w+)\}/g,
    (_match, name) => `(?<${name}>[^:]+)`,
  );

  const regex = new RegExp(`^${regexPattern}$`);
  const match = urn.match(regex);

  if (!match?.groups) {
    throw new Error(`URN "${urn}" does not match pattern "${pattern}"`);
  }

  return match.groups;
}

/**
 * Interpolate a URL template with parameters.
 *
 * @example
 * interpolate("/api/bible/{organizationId}/summary", { organizationId: "org-123" })
 * // => "/api/bible/org-123/summary"
 */
export function interpolate(
  template: string,
  params: Record<string, string>,
): string {
  return template.replace(/\{(\w+)\}/g, (_match, key) => {
    const value = params[key];
    if (value === undefined) {
      throw new Error(`Missing parameter "${key}" for URL template "${template}"`);
    }
    return encodeURIComponent(value);
  });
}

/**
 * Compile a `DomainDefinitionJSON` into the superset `DomainRuntime`.
 *
 * Pipeline:
 * 1. Merge `coreDomain` + injected `primitives` + `def.types` into ONE ArkType
 *    scope and export the typed `Module` (the generic inference layer).
 * 2. Build per-resource `ResourceAccessor`s (career lineage).
 * 3. Run the rich sub-compilers on `def` (authz rules, routes, UMA, OpenAPI).
 * 4. Assemble `get(urn)`, `checkAuthz`, `validate`, `createClient`.
 */
export function compileDomain<
  const D extends DomainDefinitionJSON,
  const P extends Record<string, unknown> = {},
>(def: D, opts?: { primitives?: P }): DomainRuntime<D, P> {
  const primitives = (opts?.primitives ?? {}) as P;

  // 1. Compile types into a single ArkType scope. The runtime object carries
  //    core + injected primitives + the domain's own types. It is fed to
  //    `scope()` typed as the accepted core+types spread shape (the bare generic
  //    `P` would otherwise trip `scope.validate` — see the header note), then the
  //    exported module is asserted to the full merged type so callers get real
  //    inferred shapes via the `DomainRuntime<D, P>` return annotation.
  const scopeInput = { ...coreDomain, ...primitives, ...def.types };
  const scopeShape = { ...coreDomain, ...def.types };
  const types = scope(scopeInput as typeof scopeShape).export() as Module<
    scope.infer<MergedScope<D, P>>
  >;

  // Runtime-only convenience for the dynamic, string-keyed `resolveType` lookups
  // (array-suffix handling). Does NOT affect caller inference — the typed module
  // is exposed via the return type above.
  const typesRecord = types as unknown as Record<string, Type>;

  function resolveType(typeRef: string): Type | undefined {
    const direct = typesRecord[typeRef];
    if (direct) return direct;

    // Array type: "SomeType[]" — resolve the base type and wrap with type().
    if (typeRef.endsWith("[]")) {
      const baseTypeName = typeRef.slice(0, -2);
      const baseType = typesRecord[baseTypeName];
      if (baseType) return type(baseType, "[]");
    }

    return undefined;
  }

  // 2. Build per-resource accessors.
  const resources: Record<string, ResourceAccessor> = {};

  for (const [name, resource] of Object.entries(def.resources)) {
    const { type: typeName } = resource;
    const rootType = resolveType(typeName);

    if (!rootType) {
      throw new Error(
        `Resource "${name}" references type "${typeName}" which is not defined in domain "${def.name}"`,
      );
    }

    resources[name] = {
      typeName,

      async fetch(params: Record<string, string>): Promise<unknown> {
        const url = interpolate(resource.fetch.urlTemplate, params);
        const response = await globalThis.fetch(url, { method: resource.fetch.method });

        if (!response.ok) {
          throw new Error(`Fetch failed: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        const validated = rootType(data);
        if (validated instanceof type.errors) {
          throw new Error(`Validation failed for ${typeName}: ${validated.summary}`);
        }
        return validated;
      },

      validate(data: unknown): ValidationResult {
        const validated = rootType(data);
        if (validated instanceof type.errors) {
          return { success: false, errors: validated.summary };
        }
        return { success: true, data: validated };
      },
    };
  }

  // 3. Rich sub-compilers (framework lineage) run on the `@4` definition.
  const authzRules = compileAuthzRules(def.resources, def.authz);
  const routes = compileRoutes(def.resources, authzRules);
  const umaResources = compileUmaResources(def.resources, def.authz);
  const openapi = compileOpenAPI(def, types);

  // 4a. get(urn) — resolve the matching resource, fetch, validate.
  async function get(urn: string): Promise<unknown> {
    for (const [, resource] of Object.entries(def.resources)) {
      let params: UrnParams;
      try {
        params = parseUrn(urn, resource.urnPattern);
      } catch {
        continue; // pattern doesn't match — try the next resource
      }

      const url = interpolate(resource.fetch.urlTemplate, params);
      const response = await globalThis.fetch(url, { method: resource.fetch.method });
      if (!response.ok) {
        throw new Error(`Fetch failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const rootType = resolveType(resource.type);
      if (!rootType) {
        throw new Error(`Type "${resource.type}" not found in domain types`);
      }
      const validated = rootType(data);
      if (validated instanceof type.errors) {
        throw new Error(`Validation failed: ${validated.summary}`);
      }
      return validated;
    }

    throw new Error(`No resource matches URN "${urn}"`);
  }

  // 4b. validate(typeName, data) — non-throwing ValidationResult (the `@4`
  //     reconciliation; every downstream consumer reads `.success`).
  function validate(typeName: string, data: unknown): ValidationResult {
    const validator = resolveType(typeName);
    if (!validator) {
      return { success: false, errors: `Type "${typeName}" not found in domain "${def.name}"` };
    }
    const validated = validator(data);
    if (validated instanceof type.errors) {
      return { success: false, errors: validated.summary };
    }
    return { success: true, data: validated };
  }

  // 4c. checkAuthz(resourceKey, context)
  function checkAuthz(resourceKey: string, context: RouteContext): AuthzResult {
    const rule = authzRules[resourceKey];
    if (!rule) {
      return { allowed: false, reason: `Unknown resource "${resourceKey}"` };
    }
    return checkAuthzRule(rule, context);
  }

  return {
    def,
    name: def.name,
    version: def.version,
    types,
    resources,
    routes,
    authzRules,
    umaResources,
    openapi,
    get,
    checkAuthz,
    validate,
    createClient: (config) => createClient(def, config),
  };
}
